import React from 'react';
import { CampaignViewSchema, CampaignViewType } from 'src/domains/casino/models/models';
import { slugToLabel } from 'src/domains/casino/utils/utils';
import { makeAutoObservable } from 'mobx';
import { RouterBackoffice } from 'src/AppState/RouterBackoffice';
import { Common } from 'src/AppState/Common';
import { PopupsStackState } from 'src/AppState/PopupsStackState';
import { CampaignCreatorState } from './CampaignCreator.state';

export class CampaignViewManager {
    public constructor(
        private router: RouterBackoffice,
        private readonly common: Common,
        private readonly campaignCreatorState: CampaignCreatorState
    ) {
        makeAutoObservable(this);
    }

    public campaignViewsList = CampaignViewSchema.options.map((view) => ({
        id: view._def.value,
        value: view._def.value,
        label: slugToLabel(view._def.value),
        notifications: 0,
    }));

    public get currentViewIndex(): number {
        if (this.router.bonusViewParam === null) {
            return 0;
        }

        return this.campaignViewsList.map((viewItem) => viewItem.value).indexOf(this.router.bonusViewParam);
    }

    public navigateToView = (viewValue: CampaignViewType): void => {
        const currentModeType = this.router.bonusCampaignParams;
        if (currentModeType?.type === 'NEW') {
            this.router.redirectToCampaign({
                type: 'NEW',
                view: viewValue,
                cloneId: currentModeType.cloneId,
            });
        }
        if (currentModeType?.type === 'EDIT') {
            this.router.redirectToCampaign({
                type: 'EDIT',
                view: viewValue,
                campaignId: currentModeType.campaignId,
            });
        }
    };

    public handlePrevView = (): void => {
        const prevViewIndex = this.currentViewIndex - 1;
        const prevView = this.campaignViewsList[prevViewIndex] ?? null;
        if (prevView !== null) {
            const prevViewValue =
                this.campaignCreatorState.generalDetailsState.campaignType === 'promo-code' &&
                prevView.value === 'action-conditions'
                    ? 'promo-code'
                    : prevView.value;

            this.navigateToView(prevViewValue);
        }
    };

    public handleNextView = (): void => {
        const nextViewIndex = this.currentViewIndex + 1;
        const nextView = this.campaignViewsList[nextViewIndex] ?? null;
        if (nextView !== null) {
            const nextViewValue =
                this.campaignCreatorState.generalDetailsState.campaignType === 'promo-code' &&
                nextView.value === 'action-conditions'
                    ? 'reward-conditions'
                    : nextView.value;

            this.navigateToView(nextViewValue);
        }
    };

    public handleDiscard = (): void => {
        const popupsStack = PopupsStackState.get(this.common);
        popupsStack.addPopup('close_campaign', {
            template: 'generic',
            title: 'Close Campaign',
            message: (
                <p className='m-0'>
                    Are you sure you want to close this campaign? Progress that was not saved will be lost.
                </p>
            ),
            hasOverlay: true,
            cancel_label: 'Cancel',
            confirm_label: 'Continue',
            onConfirm: () => {
                this.handleClose();
                popupsStack.closePopupOnTop();
            },
            onCancel: () => {
                popupsStack.closePopupOnTop();
            },
        });
    };

    public handleClose = (): void => {
        this.campaignCreatorState.resetIsFormVisited();
        this.router.redirectToBonusCampaignList();
    };

    public handleCloseWithDiscard = (): void => {
        this.campaignCreatorState.setIsFormTouched();

        if (this.campaignCreatorState.isFormTouched()) {
            this.handleDiscard();
        } else {
            this.handleClose();
        }
    };
}
